import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Package, User, Trash2, ArrowRightLeft, Loader2, QrCode } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TARGETS = ["NAPOLI", "GLS", "TIKTOK"];
const STYLE = {
  NAPOLI: { bar: "bg-violet-100 text-violet-700", border: "border-violet-500", dot: "bg-violet-500" },
  GLS: { bar: "bg-amber-100 text-amber-700", border: "border-amber-500", dot: "bg-amber-500" },
  TIKTOK: { bar: "bg-slate-800 text-white", border: "border-slate-800", dot: "bg-slate-800" },
};
const SUB = [["BRT", "Bartolini"], ["IMILE", "iMile"], ["POSTE", "Poste Italiane"], ["ALTRO", "Non riconosciuti"]];

const TiktokBreakdown = ({ breakdown }) => (
  <div className="grid grid-cols-4 gap-2" data-testid="tiktok-breakdown">
    {SUB.map(([k, label]) => (
      <div key={k} className="bg-white rounded-lg border p-2 text-center" data-testid={`tiktok-count-${k}`}>
        <div className="text-2xl font-black text-slate-900 tabular-nums">{breakdown?.[k] ?? 0}</div>
        <div className="text-[11px] text-slate-500 leading-tight">{label}</div>
      </div>
    ))}
  </div>
);

const QrPdfButton = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/shipments/tiktok-qr-pdf`, {
        responseType: "blob", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tiktok_qr_${format(new Date(), "yyyy-MM-dd")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF generato: ${res.headers["x-total-codes"] || ""} QR (BRT esclusi)`);
    } catch (e) {
      const msg = e.response?.status === 404 ? "Nessun pacco TikTok (escluso BRT) sparato oggi" : "Generazione PDF non riuscita";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button onClick={download} disabled={busy} data-testid="tiktok-qr-pdf-btn"
      className="w-full bg-[#B8912F] hover:bg-[#9c7a26] text-white font-black">
      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
      GENERA PDF QR DI OGGI (senza BRT)
    </Button>
  );
};

const MoveBar = ({ selected, carrier, onMoved, clear }) => {
  const [target, setTarget] = useState(TARGETS.find((t) => t !== carrier));
  const [busy, setBusy] = useState(false);
  const move = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/shipments/move`, { ids: selected, carrier: target },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      toast.success(`${res.data.moved} pacchi spostati in ${target}`);
      clear();
      onMoved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Spostamento non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-white border-2 border-[#B8912F] rounded-xl p-2 shadow" data-testid="move-bar">
      <span className="text-sm font-bold px-1">{selected.length} selezionati → </span>
      {TARGETS.filter((t) => t !== carrier).map((t) => (
        <button key={t} onClick={() => setTarget(t)} data-testid={`move-target-${t}`}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border ${target === t ? "bg-[#B8912F] text-white border-[#B8912F]" : "bg-white text-slate-700"}`}>{t}</button>
      ))}
      <Button size="sm" onClick={move} disabled={busy} className="ml-auto bg-[#B8912F] hover:bg-[#9c7a26] text-white font-black" data-testid="move-submit-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}SPOSTA
      </Button>
    </div>
  );
};

export const CarrierShipments = ({ carrier, shipments, count, date, searchQuery, setSearchQuery, onReload, onDelete, breakdown }) => {
  const [selected, setSelected] = useState([]);
  const st = STYLE[carrier];
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const visible = shipments.map((s) => s.id);

  return (
    <div className="space-y-3" data-testid={`carrier-list-${carrier}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca codice o nome..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid={`search-${carrier}`} />
        </div>
        <Button variant="outline" onClick={onReload}>Aggiorna</Button>
      </div>

      <div className={`flex flex-wrap items-center gap-2 px-4 py-2 rounded-lg ${st.bar}`}>
        <span className="font-bold text-lg">{count} spedizioni</span>
        <span className="text-sm opacity-80">{format(date, "dd MMMM yyyy", { locale: it })}</span>
      </div>

      {carrier === "TIKTOK" && <TiktokBreakdown breakdown={breakdown} />}
      {carrier === "TIKTOK" && <QrPdfButton />}

      {shipments.length > 0 && (
        <label className="flex items-center gap-2 px-1 text-sm text-slate-600 cursor-pointer">
          <Checkbox checked={selected.length > 0 && selected.length === visible.length}
            onCheckedChange={(v) => setSelected(v ? visible : [])} data-testid={`select-all-${carrier}`} />
          Seleziona tutti
        </label>
      )}
      {selected.length > 0 && <MoveBar selected={selected} carrier={carrier} onMoved={onReload} clear={() => setSelected([])} />}

      <div className="space-y-2">
        {shipments.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nessuna spedizione {carrier}</p>
          </div>
        ) : shipments.map((s, idx) => (
          <div key={s.id} className={`bg-white rounded-lg border-l-4 ${st.border} p-3 flex items-center gap-3 shadow-sm`} data-testid={`shipment-row-${s.id}`}>
            <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`select-shipment-${s.id}`} />
            <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-white font-bold text-xs ${st.dot}`}>{idx + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-mono font-bold text-slate-800 break-all">{s.code}</p>
              {s.customer_name && <p className="text-sm text-slate-600 flex items-center gap-1"><User className="w-3 h-3" />{s.customer_name}</p>}
              <p className="text-xs text-slate-400">{new Date(s.scanned_at).toLocaleTimeString("it-IT")}{s.notes && ` • ${s.notes}`}</p>
            </div>
            <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(s.id)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
